// docs/93 AQ10 — how a unit moves and what the water does to it, as the player reads it. The words
// are the sim's (`fk_sim::Gait::words`, pinned by a test against field.rs); whether a square is
// water and whether a swimmer is under the surface follow `field::{is_water, under_water,
// submerged}` and `Environment::class_of` exactly, so the board never shows a swimmer hidden where
// the rules see it, or a waterline on ice.
import type { FieldEnv, Snapshot, StatusTag, Survey, Tile, TileData, UnitState } from './types';

/** `fk_sim::Gait::key`. */
export type GaitKey = 'foot' | 'beast' | 'flier' | 'swimmer' | 'burrower' | 'aquatic';
export const GAIT_KEYS: readonly GaitKey[] = ['foot', 'beast', 'flier', 'swimmer', 'burrower', 'aquatic'];

const BY_VARIANT: Readonly<Record<string, GaitKey>> = { Foot: 'foot', Beast: 'beast', Flier: 'flier', Swimmer: 'swimmer', Burrower: 'burrower', Aquatic: 'aquatic' };

/** A gait as the wire carries it (`Swimmer`, or a key already) → its key; anything else walks. */
export function gaitKey(g: string | undefined): GaitKey {
  if (!g) return 'foot';
  return BY_VARIANT[g] ?? (GAIT_KEYS.includes(g as GaitKey) ? (g as GaitKey) : 'foot');
}

/** `fk_sim::Gait::words` (docs/75 plain words). */
export const GAIT_WORDS: Readonly<Record<GaitKey, string>> = {
  foot: 'Walks.',
  beast: 'Four legs: brush, mud, sand and snow slow it less.',
  flier: 'Flies.',
  swimmer: 'Swims: quick in water, slow on land.',
  burrower: 'Walks, and burrows through tunnels.',
  aquatic: 'Lives in water: cannot leave it.',
};

/** The line under a submerged unit's name (docs/93 AQ10). */
export const SUBMERGED_WORDS = 'Submerged: hard to see and hit.';

/** `Gait::swims`: at home under water, so it can submerge in deep water. */
export const swims = (g: GaitKey): boolean => g === 'swimmer' || g === 'aquatic';
export const flies = (g: GaitKey): boolean => g === 'flier';

/**
 * How a unit moves, for a card: the gait's words, or nothing for a plain walker (every hero, most
 * foes) — a card only says what is unusual about it.
 */
export function gaitLine(gait: string | undefined): string | undefined {
  const k = gaitKey(gait);
  return k === 'foot' ? undefined : GAIT_WORDS[k];
}

/** The lines a foe's card adds for how it moves and where it is (docs/93 AQ10). */
export function moveLines(unit: Pick<UnitState, 'gait' | 'statuses'>, submergedNow: boolean): string[] {
  const out: string[] = [];
  const g = gaitLine(unit.gait);
  if (g) out.push(g);
  if (submergedNow) out.push(SUBMERGED_WORDS);
  if (unit.statuses?.some((s) => s.kind === 'Floundering')) out.push(FLOUNDERING_WORDS);
  return out;
}

/** The FIELD panel's lines for the surveyed unit (docs/93 AQ10): its gait's words, then what the water is doing to it. */
export function fieldMoves(f: Pick<Survey, 'gait' | 'gaitKey' | 'gaitWords' | 'submerged' | 'floundering'>): string[] {
  const out: string[] = [];
  const k = gaitKey(f.gaitKey || f.gait);
  if (k !== 'foot') out.push(f.gaitWords || GAIT_WORDS[k]);
  if (f.submerged) out.push(SUBMERGED_WORDS);
  if (f.floundering) out.push(FLOUNDERING_WORDS);
  return out;
}

/** A floundering unit's line: what it may still do. */
export const FLOUNDERING_WORDS = 'Floundering: out of its depth; one step out, or hold.';

// ---- the water on the board -------------------------------------------------------------------

export type Water = 'shallow' | 'deep';

/** `Environment::frozen`: outdoors, never in the jungle, in a frost or in a snowfield's winter. */
export function frozenEnv(env: FieldEnv | undefined): boolean {
  if (!env) return false;
  const outdoors = env.land === 'Bog' || env.land === 'Wood' || env.land === 'Snowland' || env.land === 'Dunes' || env.land === 'Highland' || env.land === 'Jungle' || env.land === 'Underdark' || env.land === 'Ashlands';
  return outdoors && env.land !== 'Jungle' && (env.weather === 'frost' || (env.season === 'winter' && env.land === 'Snowland'));
}

/** Lands whose water is lava (`class_of`: the ember cave, and the Ashlands, docs/23 Y10). */
export const moltenLand = (land: string | undefined): boolean => land === 'Ember' || land === 'Ashlands';

/** Water a terrain is under this sky (`class_of`: ember and Ashlands water is lava, frozen shallows are ice, the deep never freezes). */
export function waterOf(t: Pick<TileData, 'terrain'> | undefined, env: FieldEnv | undefined): Water | undefined {
  if (!t || moltenLand(env?.land)) return undefined;
  if (t.terrain === 'Deep') return 'deep';
  if (t.terrain === 'Water') return frozenEnv(env) ? undefined : 'shallow';
  return undefined;
}

export type BoardLike = Pick<Snapshot['board'], 'w' | 'h' | 'tiles' | 'env'>;

/** `field::is_water` for one square (off the board is dry). */
export function waterAt(board: BoardLike, t: Tile): Water | undefined {
  if (t.x < 0 || t.y < 0 || t.x >= board.w || t.y >= board.h) return undefined;
  return waterOf(board.tiles[t.y * board.w + t.x], board.env);
}

/** The water under every square of a body (`size` a side from `tile`). */
export function bodyWater(at: (t: Tile) => Water | undefined, tile: Tile, size = 1): Array<Water | undefined> {
  const out: Array<Water | undefined> = [];
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) out.push(at({ x: tile.x + x, y: tile.y + y }));
  return out;
}

/** `field::under_water`: a swimmer whose every square is water and most of it deep. */
export function underWater(gait: GaitKey, under: ReadonlyArray<Water | undefined>): boolean {
  if (!swims(gait) || !under.length) return false;
  const deep = under.filter((w) => w === 'deep').length;
  return under.every((w) => w !== undefined) && deep * 2 > under.length;
}

const has = (tags: readonly StatusTag[] | undefined, kind: string): boolean => !!tags?.some((s) => s.kind === kind);

/** `field::submerged`: under water now and not surfaced from a strike. */
export function submergedOn(board: BoardLike, u: UnitState): boolean {
  if (u.onBoard === false || u.downed) return false;
  return !has(u.statuses, 'Surfaced') && underWater(gaitKey(u.gait), bodyWater((t) => waterAt(board, t), u.tile, u.size ?? 1));
}

// ---- the figure at the waterline (UnitSprite) ---------------------------------------------------

/**
 * How a figure meets the water (`UnitSprite`): `dry`; `wading` (a walker in water, cut at the
 * waterline); `swimming` (a swimmer in the shallows, lower in the water); `submerged` (only its back
 * shows, dark under the surface); `surfaced` (up out of the deep after a strike); `floundering`
 * (out of its depth, bobbing); `hover` (a flier standing: off the ground over its shadow).
 */
export type WaterPose = 'dry' | 'wading' | 'swimming' | 'submerged' | 'surfaced' | 'floundering' | 'hover';

export interface WaterLook {
  pose: WaterPose;
  /** The share of the figure (feet to head) under the waterline, 0..1. */
  cut: number;
  /** The deepest water it is in, if any. */
  water?: Water;
}

/** The waterline of each pose: wading a quarter deep, swimming lower, only the back of a submerged swimmer showing. */
export const CUT: Readonly<Record<WaterPose, number>> = { dry: 0, hover: 0, wading: 0.28, swimming: 0.42, surfaced: 0.38, floundering: 0.5, submerged: 0.74 };
/** A walker that strides through the deep (a lurker) is in it to the chest. */
export const CUT_DEEP_WADE = 0.46;

/**
 * The figure's pose from its gait, the water under each square of its body and its statuses. A
 * body stands in water when most of it does (a huge body half up the bank stays dry-footed); the
 * rules' submerged test decides `submerged`, so the picture never hides what the sim shows.
 */
export function waterLook(gait: GaitKey, under: ReadonlyArray<Water | undefined>, statuses: { floundering?: boolean; surfaced?: boolean } = {}): WaterLook {
  const wet = under.filter((w) => w !== undefined).length;
  const deep = under.filter((w) => w === 'deep').length;
  const water: Water | undefined = deep > 0 ? 'deep' : wet > 0 ? 'shallow' : undefined;
  if (flies(gait)) return { pose: 'hover', cut: 0, ...(water ? { water } : {}) };
  if (statuses.floundering) return { pose: 'floundering', cut: CUT.floundering, water: 'deep' };
  if (underWater(gait, under)) return statuses.surfaced ? { pose: 'surfaced', cut: CUT.surfaced, water: 'deep' } : { pose: 'submerged', cut: CUT.submerged, water: 'deep' };
  if (!water || wet * 2 <= under.length) return { pose: 'dry', cut: 0, ...(water ? { water } : {}) };
  if (swims(gait)) return { pose: 'swimming', cut: CUT.swimming, water };
  return { pose: 'wading', cut: deep * 2 > under.length ? CUT_DEEP_WADE : CUT.wading, water };
}

/** What the water does as a figure goes from one look to the next (the splash, the dive, the rise). */
export type WaterMoment = 'enter' | 'leave' | 'dive' | 'surface' | 'plunge';

export function waterMoment(was: WaterLook, now: WaterLook): WaterMoment | undefined {
  if (was.pose === now.pose && !!was.water === !!now.water) return undefined;
  if (now.pose === 'hover' || was.pose === 'hover') return undefined;
  if (now.pose === 'submerged' && was.pose !== 'submerged') return was.pose === 'dry' ? 'enter' : 'dive';
  if (was.pose === 'submerged' && now.pose !== 'submerged') return now.pose === 'dry' ? 'leave' : 'surface';
  // a walker shoved over the edge into the deep: the big splash
  if (now.pose === 'floundering' && was.pose !== 'floundering') return was.pose === 'dry' ? 'plunge' : undefined;
  if (now.pose === 'wading' && now.water === 'deep' && was.pose === 'dry') return 'plunge';
  const wetWas = was.pose !== 'dry', wetNow = now.pose !== 'dry';
  if (!wetWas && wetNow) return 'enter';
  if (wetWas && !wetNow) return 'leave';
  return undefined;
}

// ---- wakes ------------------------------------------------------------------------------------

/** Board px a figure goes through water between two wakes (a quarter of a square, more for a huge body). */
export const WAKE_EVERY = 0.28;
/** A figure that jumped further than this in one frame was set down (a warp, a redraw), not swimming. */
export const WAKE_JUMP = 0.9;

/**
 * Whether a figure moving from `prev` to `now` (board px) through water leaves a wake now: once it has
 * gone `WAKE_EVERY` squares (scaled by its size) since the last one, never across a jump. The wake
 * points back along the way it came.
 */
export function wakeStep(last: { x: number; y: number } | undefined, now: { x: number; y: number }, wet: boolean, cell: number, size = 1): { spawn: boolean; dir?: { x: number; y: number }; reset: boolean } {
  if (!last) return { spawn: false, reset: true };
  const dx = now.x - last.x, dy = now.y - last.y, d = Math.hypot(dx, dy);
  if (d > cell * size * WAKE_JUMP + cell * 0.5) return { spawn: false, reset: true };
  if (!wet) return { spawn: false, reset: true };
  if (d < cell * WAKE_EVERY * Math.max(1, size * 0.7)) return { spawn: false, reset: false };
  return { spawn: true, dir: { x: dx / d, y: dy / d }, reset: true };
}

// ---- the intent layer's shove ------------------------------------------------------------------

/**
 * docs/93 AQ8: what a shove's knockback (the projection's) does at the water: it carries the target
 * into the deep (`deep`), and it will flounder there (`flounders`: the projection set the status, or
 * a body that cannot swim lands in the deep).
 */
export function shoveIntoWater(to: Tile, target: Pick<UnitState, 'gait'> | undefined, statuses: readonly { kind: string }[], at: (t: Tile) => Water | undefined): { deep: boolean; flounders: boolean } {
  const deep = at(to) === 'deep';
  const g = gaitKey(target?.gait);
  const flounders = statuses.some((s) => s.kind === 'Floundering') || (deep && !swims(g) && !flies(g));
  return { deep, flounders };
}
